import useEventListener from "@saleor/hooks/useEventListener";
import { makeStyles } from "@saleor/macaw-ui";
import React, { RefObject, useEffect, useRef } from "react";

interface StickyVerticalScrollBarProps {
  container: RefObject<HTMLDivElement>;
  reRender?: boolean;
  right?: number;
}

interface propsType {
  right?: number;
}

const useStyles = makeStyles<propsType>(
  () => ({
    root: {
      zIndex: 1000,
      display: "none",
      position: "fixed",
      right: props => `${props.right || 0}px`,
      overflowY: "auto",
      "&::-webkit-scrollbar": {
        width: "14px",
        background: "#FFFFFF"
      },
      "&::-webkit-scrollbar-track": {
        borderRadius: 10
      },
      "&::-webkit-scrollbar-thumb": {
        background: "#CCCCCC",
        borderRadius: 10,
        border: "4px solid #FFFFFF",
        width: "6px"
      }
    }
  }),
  { name: "StickyVerticalScrollBar" }
);

function checkPosition(
  container: RefObject<HTMLDivElement>,
  fixedBarTemplate: RefObject<HTMLDivElement>,
  rightFixed?: number
) {
  const containerOffset = {
    top: container.current.offsetTop,
    bottom: container.current.offsetTop + container.current.offsetHeight
  };
  const windowOffset = {
    top: window.scrollY,
    bottom: window.scrollY + window.innerHeight - rightFixed * 2
  };

  if (
    containerOffset.top > windowOffset.bottom ||
    windowOffset.bottom > containerOffset.bottom ||
    window.innerWidth -
      container.current?.getBoundingClientRect()?.right -
      rightFixed >
      0
  ) {
    fixedBarTemplate.current.style.display = "none";
  } else {
    fixedBarTemplate.current.style.display = "block";
    fixedBarTemplate.current.scrollTop = container.current.scrollTop;
  }
}

const StickyVerticalScrollBar: React.FC<StickyVerticalScrollBarProps> = ({
  container,
  reRender = false,
  right = 0
}) => {
  const fixedBarTemplate = useRef<HTMLDivElement | null>(null);
  const scrollBar = useRef<HTMLDivElement | null>(null);
  const classes = useStyles({ right });

  useEffect(() => {
    if (scrollBar.current && container.current) {
      scrollBar.current.style.height = `${container.current.scrollHeight}px`;
      scrollBar.current.style.height = `${container.current.scrollWidth}px`;
      scrollBar.current.scrollTop = container.current.scrollTop;
      fixedBarTemplate.current.style.height = `${container.current.offsetHeight}px`;
      checkPosition(container, fixedBarTemplate, right);
    }
  }, [scrollBar, container, reRender]);

  useEventListener(
    "scroll",
    () => {
      container.current.scrollTop = fixedBarTemplate.current.scrollTop;
    },
    fixedBarTemplate
  );

  useEventListener("scroll", () => {
    checkPosition(container, fixedBarTemplate, right);
  });

  return (
    <div className={classes.root} ref={fixedBarTemplate}>
      <div ref={scrollBar}></div>
    </div>
  );
};

export default StickyVerticalScrollBar;
